/**
 * Redux slice for application configuration
 * Handles field configuration from backend
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  configService,
  FieldsConfig,
} from '../services/configService';
import { LOADING_STATE } from '../types/constants';

// ===== TYPES =====

interface ConfigState {
  // Field configuration from backend
  fieldsConfig: FieldsConfig | null;

  // Loading state
  fetchFieldsStatus: LOADING_STATE;

  // Error state
  fieldsError: string | null;

  // Timestamp of last successful fetch
  lastFetchedAt: number | null;
}

// ===== INITIAL STATE =====

const initialState: ConfigState = {
  fieldsConfig: null,
  fetchFieldsStatus: 'idle',
  fieldsError: null,
  lastFetchedAt: null,
};

// ===== ASYNC THUNKS =====

/**
 * Fetch field configuration from backend
 */
export const fetchFieldsConfig = createAsyncThunk(
  'config/fetchFields',
  async (useCase: string = 'incident_report', { rejectWithValue }) => {
    try {
      return await configService.fetchFieldsConfig(useCase);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch field configuration');
    }
  }
);

/**
 * Fetch all configuration (convenience thunk)
 * Simplified to only fetch field config
 */
export const fetchAllConfig = createAsyncThunk(
  'config/fetchAll',
  async (_, { dispatch }) => {
    await dispatch(fetchFieldsConfig('incident_report'));
  }
);

// ===== SLICE =====

const configSlice = createSlice({
  name: 'config',
  initialState,
  reducers: {
    /**
     * Clear all configuration (useful for logout)
     */
    clearConfig: (state) => {
      state.fieldsConfig = null;
      state.lastFetchedAt = null;
    },

    /**
     * Clear errors
     */
    clearErrors: (state) => {
      state.fieldsError = null;
    },
  },
  extraReducers: (builder) => {
    // Fields config
    builder
      .addCase(fetchFieldsConfig.pending, (state) => {
        state.fetchFieldsStatus = 'pending';
        state.fieldsError = null;
      })
      .addCase(fetchFieldsConfig.fulfilled, (state, action: PayloadAction<FieldsConfig>) => {
        state.fetchFieldsStatus = 'fulfilled';
        state.fieldsConfig = action.payload;
        state.lastFetchedAt = Date.now();
      })
      .addCase(fetchFieldsConfig.rejected, (state, action) => {
        state.fetchFieldsStatus = 'rejected';
        state.fieldsError = action.payload as string;
      });
  },
});

// ===== EXPORTS =====

export const { clearConfig, clearErrors } = configSlice.actions;
export default configSlice.reducer;
